import { Fragment, MouseEvent, ReactElement, useEffect, useMemo, useState } from 'react'
import { Box, Container, Typography, Button, Grid, CircularProgress } from '@mui/material'
import WhatshotOutlinedIcon from '@mui/icons-material/WhatshotOutlined'
import { motion } from 'framer-motion'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'
import Link from 'next/link'
import Head from 'next/head'
import dynamic from 'next/dynamic'
import axios from 'axios'
import { useQuery } from 'react-query'
import { BASE_API_URL } from '@/configs'
import { usePartnerPageDataStore } from '@/store/store'
import { GRAY_COLORS_VARIANTS, TEXT_DARK_COLORS_VARIANTS } from '@/utils/constants/colors'
import { GameINFOV2, PartnerPageData, SimpleRound } from 'types/types'
import {
  checkContrast,
  isBattleRoyaleGame,
  isRoundJoined,
  itemKey,
  stringToColor,
} from 'types/utils'
import { getPartnerIdentifier } from '@/utils/getPartnerIdentifier'
import { FlowTypes } from '@/lib/constants'
import { useAuth } from 'context/JWTContext'
import LogoutIcon from '@mui/icons-material/Logout'
import BorderColorIcon from '@mui/icons-material/BorderColor'
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import SegmentIcon from '@mui/icons-material/Segment'
import utc from 'dayjs/plugin/utc'
import { UserApi } from 'api/api'
import { useRouter } from 'next/router'
import { FilteringModule } from 'components/shared/filtering-module/FilteringModule'
import { FILTERING_MODULE_ELEMENTS } from '@/utils/constants/prizeDrawConsts'
import { FilteringByStatus } from 'components/shared/filtering-status-module/FilteringByStatus'
import { usePartnerGames, PartnerGamesResponse, Game } from 'hooks/usePartnerGames'
import { GameCard, ROUND_STATUS, ExpandedGame } from 'components/GameCard'
import { BattleRoyaleGameCard } from 'components/battle-royale/BattleRoyaleGameCard'
import { HighlightedCard } from 'components/shared/HighlightedCard'
import { ProfileDrawer } from 'components/prize-draws/profile-drawer/ProfileDrawer'
import { ProductSwitchButton } from 'components/shared/ProductSwitchButton'
import { HIDDEN_WITH_BOTTOM_NAV } from 'components/shared/bottomNavStyles'
import { ChangeEmailModal } from 'components/modals/ChangeEmailModal'
import { SocialProofBanner } from 'components/SocialProofBanner'
import { isTBLPage } from '@/utils/tblUtils'
import { getCharityPath } from '@/utils/charitySlug'
import { ChangeNicknameModal } from '../../../components/ChangeNicknameModal'
import SportIcon from '../../../components/SportIcon'
import UserEmailPreferences from '../../../components/modals/UserEmailPreferences'
import StarSportsLanding from '../../../components/StarSportsLanding'
import Footer from '../../../components/Footer'

const Banner = dynamic(() => import('components/Banner'), { ssr: false })

dayjs.extend(utc)
dayjs.extend(duration)

const STATUS_ELEMENTS = [
  { statusId: 0, statusLabel: 'Upcoming' },
  { statusId: 1, statusLabel: 'Live', isLive: true },
  { statusId: 2, statusLabel: 'Finished' },
]

const LABEL_TO_STATUS: Record<string, 'UPCOMING' | 'ACTIVE' | 'FINISHED'> = {
  Upcoming: 'UPCOMING',
  Live: 'ACTIVE',
  Finished: 'FINISHED',
}

const STATUS_TO_ID: Record<string, number> = {
  UPCOMING: 0,
  ACTIVE: 1,
  FINISHED: 2,
}

type AllFilterElement = (typeof FILTERING_MODULE_ELEMENTS)[number]
type SportFilterElement = {
  id: number
  label: string
  icon: ReactElement
}
type TournamentFilterElement = {
  id: number
  label: string
  tournament_name: string | undefined
  sport_name: string | undefined
  icon: ReactElement
}
type SportFilterElementUnion = AllFilterElement | SportFilterElement | TournamentFilterElement

const toTime = (date: string | null | undefined): number | null => {
  if (!date) return null
  const time = new Date(date).getTime()
  return Number.isFinite(time) ? time : null
}

// Earliest round by start date in one pass. Ties and invalid dates fall back to
// array order: first round for earliest, last round for latest.
const pickRoundByStart = (rounds: SimpleRound[], latest: boolean): SimpleRound | undefined =>
  rounds.reduce<SimpleRound | undefined>((best, round) => {
    if (!best) return round
    const time = toTime(round.start_date)
    const bestTime = toTime(best.start_date)
    if (time === null) return latest && bestTime === null ? round : best
    if (bestTime === null || time === bestTime) return bestTime === null || latest ? round : best
    return (latest ? time > bestTime : time < bestTime) ? round : best
  }, undefined)

// Matches the start time GameCard's countdown displays, so order and countdown agree
const tileStartTime = (tile: ExpandedGame): number | null =>
  toTime(
    tile.kind === 'round'
      ? tile.roundStartDate
      : (tile.has_multiple_rounds && tile.next_round_start_time) || tile.deadline,
  )

const TILE_ORDER: Record<string, { enteredFirst: boolean; direction: 1 | -1 }> = {
  UPCOMING: { enteredFirst: false, direction: 1 },
  ACTIVE: { enteredFirst: true, direction: 1 },
  FINISHED: { enteredFirst: true, direction: -1 },
}

function PartnerGamesPage({
  page,
  shouldRenderDarkLogo,
  game,
  gamesData,
  error: serverError,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const router = useRouter()
  const currentSport = router.isReady ? (router.query.sport as string | undefined) : undefined

  useEffect(() => {
    if (router.isReady && !router.query.status) {
      router.replace(
        { pathname: router.pathname, query: { ...router.query, status: 'UPCOMING' } },
        undefined,
        { shallow: true },
      )
    }
  }, [router.isReady, router.query.status, router.replace])

  const sportFilterElements = useMemo(() => {
    const toTitleCase = (str: string) =>
      str.replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())

    const allFilter = FILTERING_MODULE_ELEMENTS.filter((el) => el.label === 'All')

    // Only surface the pill when the partner actually has a Battle Royale
    // game live - otherwise it's a filter for a game type that doesn't exist
    // on this page.
    const hasBattleRoyaleGames = (gamesData?.games ?? []).some(isBattleRoyaleGame)
    const battleRoyaleFilter = hasBattleRoyaleGames
      ? [
          {
            id: 'battle-royale',
            label: 'Battle Royale',
            icon: <WhatshotOutlinedIcon sx={{ fontSize: '1.7rem' }} />,
          },
        ]
      : []

    if (!gamesData?.filters?.sports) return [...allFilter, ...battleRoyaleFilter]

    const sports = gamesData.filters.sports
      .map((sport, i) => {
        const known = FILTERING_MODULE_ELEMENTS.find(
          (el) => el.label?.toLowerCase() === sport.name?.toLowerCase(),
        )
        return {
          id: known?.id ? `sport-${known.id}` : `temp-sport-${i}`,
          label: known?.label ?? toTitleCase(sport.name),
          icon: known?.icon ?? <SportIcon sportName={sport.name} sx={{ fontSize: '1.7rem' }} />,
        }
      })
      .sort((a, b) => a.label.localeCompare(b.label))

    if (!gamesData?.filters?.tournaments?.length) {
      return [...allFilter, ...battleRoyaleFilter, ...sports]
    }

    const tournaments = gamesData.filters.tournaments
      .filter((t) => !!t.image_url)
      .map((tournament, i) => {
        const matchingGame = (gamesData?.games ?? []).find(
          (games) => games.tournament_name === tournament.name,
        )
        return {
          id: `tournament-${i}`,
          label: tournament.name || 'Tournament',
          tournament_name: tournament.name,
          sport_name: matchingGame?.sport_name ?? undefined,
          icon: (
            <img
              src={tournament.image_url!}
              alt={tournament.name}
              style={{ width: '1.8rem', height: '1.8rem', objectFit: 'contain' }}
            />
          ),
          isTournament: true,
        }
      })
      .sort((a, b) => a.label.localeCompare(b.label))
    if (page.feature_flags.is_tbl_page) {
      return [...allFilter, ...battleRoyaleFilter, ...tournaments, ...sports]
    }
    return [...allFilter, ...battleRoyaleFilter, ...sports, ...tournaments]
  }, [gamesData?.filters, gamesData?.games])

  const isBattleRoyaleFilterActive = router.isReady && router.query.battleRoyale === 'true'

  const activeFilterId = useMemo(() => {
    const currentTournament = router.isReady && (router.query.tournament as string | undefined)
    const allFilterId = FILTERING_MODULE_ELEMENTS.find((el) => el.label === 'All')?.id

    if (isBattleRoyaleFilterActive) return 'battle-royale'
    if (currentTournament) {
      return (sportFilterElements as SportFilterElementUnion[]).find(
        (el) => 'tournament_name' in el && el.tournament_name === currentTournament,
      )?.id
    }
    if (!currentSport) return allFilterId
    return (
      (sportFilterElements as SportFilterElementUnion[]).find(
        (el) => el.label === currentSport && !('tournament_name' in el),
      )?.id ?? allFilterId
    )
  }, [
    currentSport,
    sportFilterElements,
    router.isReady,
    router.query.tournament,
    isBattleRoyaleFilterActive,
  ])

  const handleFilterChange = (id: string | number) => {
    const { sport: _s, tournament: _t, battleRoyale: _br, ...restQuery } = router.query
    const currentStatus = (router.query.status as string) || 'UPCOMING'

    if (id === 'battle-royale') {
      router.push(
        {
          pathname: router.pathname,
          query: { ...restQuery, status: currentStatus, battleRoyale: 'true' },
        },
        undefined,
        { shallow: true },
      )
      return
    }

    const selected = (sportFilterElements as SportFilterElementUnion[]).find((el) => el.id === id)

    if (!selected || selected.label === 'All') {
      router.push(
        { pathname: router.pathname, query: { ...restQuery, status: currentStatus } },
        undefined,
        { shallow: true },
      )
      return
    }

    let tournamentName: string | undefined
    let sportName: string | undefined

    if ('tournament_name' in selected) {
      tournamentName = selected.tournament_name
      sportName = selected.sport_name
    } else {
      sportName = selected.label
    }

    const newQuery = {
      ...restQuery,
      status: currentStatus,
      ...(sportName && { sport: sportName }),
      ...(tournamentName && { tournament: tournamentName }),
    }
    router.push({ pathname: router.pathname, query: newQuery }, undefined, { shallow: true })
  }

  const handleStatusChange = (id: number) => {
    const { status: _status, ...restQuery } = router.query
    const selected = STATUS_ELEMENTS.find((el) => el.statusId === id)
    const status = selected ? LABEL_TO_STATUS[selected.statusLabel] : 'UPCOMING'
    router.push({ pathname: router.pathname, query: { ...restQuery, status } }, undefined, {
      shallow: true,
    })
  }

  const activeStatusId = router.isReady ? STATUS_TO_ID[router.query.status as string] ?? 0 : 0
  const [setPartnerPageData] = usePartnerPageDataStore((state) => [state.setPartnerPageData])

  useEffect(() => {
    setPartnerPageData({ ...page, is_user_joined: false })
  }, [page, setPartnerPageData])

  const { user, isAuthenticated, logout } = useAuth()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [modalState, setModalState] = useState({
    isChangeUsernameOpen: false,
    isChangeEmailOpen: false,
    isUserPreferencesOpen: false,
  })
  const [isContactUsOpen, setIsContactUsOpen] = useState(false)
  const isStarSportsPage = page.feature_flags.is_star_sports_page

  const toggleModalState = (field: keyof typeof modalState) => {
    setModalState({ ...modalState, [field]: !modalState[field] })
  }

  const primaryColor = '#FFC107'
  const secondaryColor = '#FFB300'
  const contentColor = '#0a1e28'

  const initialGames = useMemo(() => gamesData?.games ?? [], [gamesData?.games])
  const { games: filteredGames, loading: gamesLoading } = usePartnerGames(
    gamesData?.partner_id ?? 0,
    initialGames,
  )

  const authenticatedGamesQuery = useQuery<PartnerGamesResponse>(
    ['partnerGamesAuthed', gamesData?.partner_id, user?.id],
    () => UserApi.getPartnerGamesAuthed(gamesData?.partner_id ?? 0),
    {
      enabled: isAuthenticated && !!user?.id && !!gamesData?.partner_id,
      staleTime: 1000 * 60 * 5,
    },
  )

  const authedGamesMap = useMemo(() => {
    const map: Record<number, Game> = {}
    authenticatedGamesQuery.data?.games.forEach((g: Game) => {
      map[g.id] = g
    })
    return map
  }, [authenticatedGamesQuery.data])
  const currentStatus = router.isReady ? (router.query.status as string) || 'UPCOMING' : 'UPCOMING'

  const gamesWithStatus = useMemo(() => {
    if (!filteredGames?.length) return []

    const authedResolved = authenticatedGamesQuery.isSuccess

    return filteredGames.map((games) => {
      const authedGame = authedGamesMap[games.id]
      return {
        ...games,
        is_user_joined: authedResolved ? authedGame?.is_user_joined ?? false : false,
        next_round_is_user_joined: authedResolved
          ? authedGame?.next_round_is_user_joined ?? undefined
          : undefined,
        rounds_joined: authedResolved ? authedGame?.rounds_joined : [],
        // position/score are per-user and only ever populated on the authed response
        position: authedResolved ? authedGame?.position ?? null : null,
        score: authedResolved ? authedGame?.score ?? null : null,
        winner_name: authedResolved ? authedGame?.winner_name ?? null : null,
        winner_score: authedResolved ? authedGame?.winner_score ?? null : null,
      }
    })
  }, [filteredGames, authedGamesMap, authenticatedGamesQuery.isSuccess])

  // Expand multi-round games into separate round tiles
  const allExpandedGames = useMemo(() => {
    const expanded: ExpandedGame[] = []

    gamesWithStatus.forEach((game) => {
      const isJoined = (roundId: number) => isRoundJoined(game, roundId, game.is_user_joined)

      if (game.has_multiple_rounds && game.rounds?.length) {
        const { rounds } = game

        const activeRounds = rounds.filter((r) => r.status === ROUND_STATUS.ACTIVE)
        const upcomingRounds = rounds.filter((r) => r.status === ROUND_STATUS.UPCOMING)
        const finishedRounds = rounds.filter((r) => r.status === ROUND_STATUS.FINISHED)

        // One tile per active round
        activeRounds.forEach((round) => {
          expanded.push({
            ...game,
            kind: 'round',
            roundId: round.id,
            roundName: round.name,
            roundStatus: ROUND_STATUS.ACTIVE,
            roundStartDate: round.start_date,
            roundIndex: rounds.indexOf(round),
            is_user_joined: isJoined(round.id),
          })
        })

        const nextUpcoming = pickRoundByStart(upcomingRounds, false)
        if (nextUpcoming) {
          expanded.push({
            ...game,
            kind: 'round',
            roundId: nextUpcoming.id,
            roundName: nextUpcoming.name,
            roundStatus: ROUND_STATUS.UPCOMING,
            roundStartDate: nextUpcoming.start_date,
            roundIndex: rounds.indexOf(nextUpcoming),
            is_user_joined: isJoined(nextUpcoming.id),
          })
        }

        const mostRecent = pickRoundByStart(finishedRounds, true)
        if (mostRecent) {
          expanded.push({
            ...game,
            kind: 'round',
            roundId: mostRecent.id,
            roundName: mostRecent.name,
            roundStatus: ROUND_STATUS.FINISHED,
            roundStartDate: mostRecent.start_date,
            roundIndex: rounds.indexOf(mostRecent),
            is_user_joined: isJoined(mostRecent.id),
          })
        }
      } else {
        expanded.push({
          ...game,
          kind: 'game',
          is_user_joined: game.is_user_joined,
        })
      }
    })

    return expanded
  }, [gamesWithStatus])

  // Tile hierarchy is round-specific: each round tile ranks on its own join status and
  // start time, not its parent game's.
  const expandedGamesWithRounds = useMemo(() => {
    const byStatus = allExpandedGames.filter((game) =>
      game.kind === 'round' ? game.roundStatus === currentStatus : game.status === currentStatus,
    )
    const filtered = isBattleRoyaleFilterActive
      ? byStatus.filter((game) => isBattleRoyaleGame(game))
      : byStatus

    const order = TILE_ORDER[currentStatus]
    if (!order) return filtered

    return filtered
      .map((tile) => ({ tile, time: tileStartTime(tile) }))
      .sort((a, b) => {
        if (a.tile.is_user_joined !== b.tile.is_user_joined) {
          return a.tile.is_user_joined === order.enteredFirst ? -1 : 1
        }
        if (a.time === null && b.time === null) return 0
        if (a.time === null) return 1
        if (b.time === null) return -1
        return order.direction * (a.time - b.time)
      })
      .map(({ tile }) => tile)
  }, [allExpandedGames, currentStatus, isBattleRoyaleFilterActive])

  // Highlighted game: the partner's designated game (page.highlighted_game_id) if it is
  // active or upcoming, otherwise the upcoming game with the closest starting time.
  const highlightedGame = useMemo(() => {
    const games = gamesData?.games ?? []
    if (!games.length) return null

    const startTimeOf = (g: Game) =>
      g.has_multiple_rounds && g.next_round_start_time ? g.next_round_start_time : g.deadline

    const designated = games.find(
      (g) =>
        g.id === page.highlighted_game_id && (g.status === 'ACTIVE' || g.status === 'UPCOMING'),
    )
    if (designated) return designated

    const upcoming = games
      .filter((g) => g.status === 'UPCOMING')
      .sort((a, b) => new Date(startTimeOf(a)).getTime() - new Date(startTimeOf(b)).getTime())

    return upcoming[0] ?? null
  }, [gamesData?.games, page.highlighted_game_id])

  const isLoadingJoinStatus = authenticatedGamesQuery.isLoading

  // if (isStarSportsPage) {
  //   return <StarSportsLanding page={page} shouldRenderDarkLogo={shouldRenderDarkLogo} game={game} />
  // }

  const correctLoginPath = page.is_external_login
    ? `/${page.company_domain}/auth/external-login`
    : `/${page.company_domain}/auth/email-check`

  if (serverError) {
    return (
      <>
        <Head>
          <meta name="theme-color" content={contentColor} />
        </Head>
        <Box
          sx={{
            minHeight: '100vh',
            background: `linear-gradient(180deg, ${contentColor} 0%, #051119 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ color: '#ff6b6b', fontSize: '1.25rem', mb: 3 }}>
              Error: {serverError}
            </Typography>
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
              sx={{
                bgcolor: primaryColor,
                color: '#000',
                fontWeight: 700,
                px: 4,
                py: 1.5,
                '&:hover': { bgcolor: secondaryColor },
              }}
            >
              Retry
            </Button>
          </Box>
        </Box>
      </>
    )
  }

  if (!gamesData || gamesData.games.length === 0) {
    return (
      <>
        <Head>
          <meta name="theme-color" content={contentColor} />
        </Head>
        <Box
          sx={{
            minHeight: '100vh',
            background: page.landing_bg_color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '1.25rem' }}>
            No games available
          </Typography>
        </Box>
      </>
    )
  }

  const renderHeaderAction = () => {
    if (!isAuthenticated && isStarSportsPage) {
      return <></>
    }
    if (!isAuthenticated) {
      return (
        <>
          <Link
            href={{
              pathname: correctLoginPath,
              query: { flowType: FlowTypes.AUTH, redirectBackToLanding: 'true' },
            }}
            passHref
            style={{ textDecoration: 'none' }}
          >
            <Button
              variant="contained"
              sx={{
                bgcolor: primaryColor,
                color: '#000',
                fontWeight: 650,
                fontSize: { xs: '0.875rem', md: '1rem', lg: '1.125rem' },
                px: { xs: '12px', md: 2 },
                py: 1,
                borderRadius: '10px',
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(255, 193, 7, 0.3)',
                '&:hover': {
                  bgcolor: primaryColor,
                  filter: 'brightness(0.95)',
                },
                ...HIDDEN_WITH_BOTTOM_NAV,
              }}
            >
              Sign In
            </Button>
          </Link>
        </>
      )
    }

    const getInitials = () => {
      if (!user?.nickname) return '??'
      const names = user.nickname.split(' ')
      if (names.length >= 2) {
        return `${names[0][0]}${names[1][0]}`.toUpperCase()
      }
      return user.nickname.substring(0, 2).toUpperCase()
    }

    const handleClick = (event: MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget)
    }

    const handleClose = () => {
      setAnchorEl(null)
    }

    const handleLogout = () => {
      handleClose()
      logout(`/${page.company_domain}/landing`)
    }

    const allMenuItems = [
      {
        key: 'charity',
        label: page.charity_page_breadcrumb_name || 'Donate',
        icon: <SegmentIcon fontSize="small" sx={{ transform: 'rotateY(180deg)' }} />,
        onClick: () => window.open(getCharityPath(page), '_self'),
        visible: page.feature_flags.sign_up_charity_page,
        marginTop: true,
      },
      // {
      //   key: 'prefs',
      //   label: 'Email notifications',
      //   icon: <NotificationsIcon fontSize="small" />,
      //   onClick: () => toggleModalState('isUserPreferencesOpen'),
      //   visible: !isStarSportsPage,
      // },
      {
        key: 'name',
        label: 'Change name',
        icon: <BorderColorIcon fontSize="small" />,
        onClick: () => toggleModalState('isChangeUsernameOpen'),
        visible: !isStarSportsPage,
      },
      {
        key: 'email',
        label: 'Change email',
        icon: <MailOutlineIcon fontSize="small" />,
        onClick: () => toggleModalState('isChangeEmailOpen'),
        visible: !isStarSportsPage,
      },
      {
        key: 'contact',
        label: 'Contact us',
        icon: <AlternateEmailIcon fontSize="small" />,
        onClick: () => setIsContactUsOpen(true),
        visible: true,
      },
      {
        key: 'logout',
        label: 'Log Out',
        icon: <LogoutIcon fontSize="small" />,
        onClick: handleLogout,
        visible: true,
      },
    ]

    const menuItems = allMenuItems.filter((item) => item.visible)

    return <>{!isStarSportsPage && <ProfileDrawer pageTheme={page} />}</>
  }

  return (
    <>
      <Head>
        <meta name="theme-color" content={contentColor} />
        <title>{page.site_title || 'Partner Games'}</title>
      </Head>
      <Banner />
      <Box
        sx={{
          minHeight: '100vh',
          background: page.landing_bg_color,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url(${page.landing_image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.7,
            zIndex: -1,
            pointerEvents: 'none',
          }}
        />
        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <Container maxWidth="xl">
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                minHeight: { xs: 92, sm: 90, md: 110 },
                py: 1,
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 2,
                  minWidth: { xs: '104px', sm: '104px', md: '130px' },
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {page.feature_flags.is_tbl_page && (
                  <ProductSwitchButton
                    href={`/${page.company_domain}/prize-draw`}
                    product="prize-draws"
                  />
                )}
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  display: 'flex',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  maxWidth: {
                    xs: 'calc(100% - 150px)',
                    sm: 'calc(100% - 208px)',
                    md: 'calc(100% - 260px)',
                  },
                  px: 1,
                }}
              >
                <Box
                  component="img"
                  src={page.landing_logo ?? ''}
                  alt="company-logo"
                  sx={{
                    height: 'auto',
                    width: '100%',
                    maxHeight: { xs: '68px', sm: '76px', md: '90px' },
                    maxWidth: { xs: '260px', sm: '292px', md: '346px' },
                    objectFit: 'contain',
                  }}
                />
              </Box>
              <Box
                sx={{
                  position: 'relative',
                  minWidth: { xs: '100px', sm: '104px', md: '130px' },
                  flexShrink: 0,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: { xs: 1, sm: 1.3, md: 2 },
                  alignItems: 'center',
                  zIndex: 10,
                }}
              >
                {renderHeaderAction()}
              </Box>
            </Box>
          </Container>

          {/* Hero Section */}
          <Container maxWidth="xl">
            <Box sx={{ textAlign: 'center', position: 'relative', pt: 1, pb: 1 }}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.875rem', lg: '3.25rem' },
                    fontWeight: 800,
                    color: 'white',
                    lineHeight: 1,
                    mt: 1,
                    mb: 1,
                    fontStyle: isStarSportsPage ? 'normal' : 'italic',
                    textTransform: 'uppercase',
                    fontFamily: page.fonts?.main_font?.family || 'inherit',
                  }}
                >
                  {page.header_line_1}
                </Typography>
                {!isTBLPage(page) && (
                  <Typography
                    sx={{
                      fontSize: { xs: '1rem', md: '1.25rem' },
                      fontWeight: 600,
                      color: 'rgba(255,255,255,0.7)',
                      maxWidth: '600px',
                      margin: '0 auto',
                      lineHeight: 1.6,
                      fontFamily: page.fonts?.secondary_font?.family || 'inherit',
                    }}
                  >
                    {page.promotional_text_1}
                  </Typography>
                )}
              </motion.div>
            </Box>
          </Container>

          {isTBLPage(page) && (
            <Container maxWidth="xl" sx={{ pb: 3, pt: 0 }}>
              <SocialProofBanner page={page} />
            </Container>
          )}

          {/* Highlighted game */}
          {page.feature_flags.show_game_featured_card && highlightedGame && (
            <Container maxWidth="xl" sx={{ pb: 3, pt: 0 }}>
              <HighlightedCard
                href={`/${page.company_domain}/games/${highlightedGame.id}`}
                title={highlightedGame.title}
                subtitle={highlightedGame.description || highlightedGame.prize || undefined}
                logoSrc={page.game_featured_image || page.logo || page.landing_logo || undefined}
                baseColor={page.background_color}
                badgeColor={GRAY_COLORS_VARIANTS[50]}
                badgeTextColor={TEXT_DARK_COLORS_VARIANTS[500]}
                titleColor={primaryColor}
                subtitleColor={primaryColor}
                fontFamily={page.fonts?.main_font?.family}
              />
            </Container>
          )}

          {/* Sport Filter */}
          <Container maxWidth="xl" sx={{ pb: 3, pt: 0 }}>
            <Box sx={{ position: 'relative' }}>
              {(gamesLoading || (isAuthenticated && isLoadingJoinStatus)) && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1,
                  }}
                >
                  <CircularProgress size={24} sx={{ color: primaryColor }} />
                </Box>
              )}
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={3}
                sx={{
                  opacity: gamesLoading || (isAuthenticated && isLoadingJoinStatus) ? 0.9 : 1,
                }}
              >
                <FilteringModule
                  elements={sportFilterElements}
                  onFilterChange={handleFilterChange}
                  initialActiveId={activeFilterId}
                  sx={{
                    width: '100%',
                    maxWidth: '100%',
                    mx: 'auto',
                    '& > .MuiBox-root': {
                      display: 'flex',
                      overflowX: 'auto',
                      overflowY: 'hidden',
                      alignItems: 'stretch',
                      my: 0,
                      justifyContent: 'safe center',
                      '::-webkit-scrollbar': {
                        height: '4px',
                        width: '4px',
                      },
                      '::-webkit-scrollbar-thumb:horizontal': {
                        background: '#888',
                        borderRadius: '10px',
                      },
                    },
                    '& .MuiButton-root': {
                      textTransform: 'none',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      minWidth: '3.5rem',
                      width: 'auto',
                      height: 'auto',
                      flexShrink: 0,
                      gap: '4px',
                      px: 0.5,
                      pt: 0.75,
                      pb: 0.75,
                      '& .MuiBox-root:first-of-type': {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '2.25rem',
                        height: '2.25rem',
                        flexShrink: 0,
                        '& .MuiTypography-root': {
                          fontSize: '1.35rem',
                          mt: 0,
                        },
                      },
                    },
                    '& .MuiTypography-root': {
                      textTransform: 'none',
                      fontSize: '0.75rem',
                      whiteSpace: 'normal',
                      textAlign: 'center',
                      width: '100%',
                      maxWidth: '4.5rem',
                      lineHeight: 1.2,
                      mt: 'auto',
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    },
                  }}
                />
                <FilteringByStatus
                  elements={STATUS_ELEMENTS}
                  onFilterChange={handleStatusChange}
                  initialActiveId={activeStatusId}
                />
              </Box>
            </Box>
          </Container>

          {/* Games Grid */}
          <Container maxWidth="xl" sx={{ pb: 12 }}>
            {isLoadingJoinStatus && isAuthenticated && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <CircularProgress size={24} sx={{ color: primaryColor }} />
              </Box>
            )}
            <Grid container spacing={3} sx={{ alignItems: 'stretch' }}>
              {expandedGamesWithRounds.map((games, index) => {
                const CardComponent = isBattleRoyaleGame(games) ? BattleRoyaleGameCard : GameCard
                return (
                  <Grid item xs={12} sm={6} lg={4} key={itemKey(games)} sx={{ display: 'flex' }}>
                    <CardComponent
                      game={games}
                      index={index}
                      page={page}
                      primaryColor={primaryColor}
                      secondaryColor={secondaryColor}
                      isAuthenticated={isAuthenticated}
                      isLoadingJoinStatus={isLoadingJoinStatus}
                    />
                  </Grid>
                )
              })}
            </Grid>
            {expandedGamesWithRounds.length === 0 && !gamesLoading && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '20px' }}>
                  No games found for the selected filters
                </Typography>
              </Box>
            )}

            {/* Sign up */}
            {isStarSportsPage && !isAuthenticated && (
              <Box bgcolor={page.background_color} m={5} textAlign="center">
                <Typography
                  variant="body2"
                  style={{
                    color: primaryColor,
                    fontStyle: 'bold',
                    textDecoration: 'none',
                    fontSize: '1.25rem',
                  }}
                >
                  You need an active Star Sports account to play. Not got one?{' '}
                  <Link
                    href="https://go.starsports.bet/visit/?bta=35056&nci=5358"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Typography
                      variant="body2"
                      component="span"
                      style={{
                        color: primaryColor,
                        fontStyle: 'bold',
                        textDecoration: 'underline',
                        marginLeft: '4px',
                        fontSize: '1.25rem',
                      }}
                      fontFamily={page.fonts.secondary_font}
                    >
                      Sign up here.
                    </Typography>
                  </Link>
                </Typography>
              </Box>
            )}
          </Container>
        </Box>
      </Box>

      <Footer page={page} marginTop="0" />

      {modalState.isChangeUsernameOpen && (
        <ChangeNicknameModal
          isOpen={modalState.isChangeUsernameOpen}
          setIsOpen={() => toggleModalState('isChangeUsernameOpen')}
        />
      )}

      {modalState.isChangeEmailOpen && (
        <ChangeEmailModal
          isOpen={modalState.isChangeEmailOpen}
          setIsOpen={() => toggleModalState('isChangeEmailOpen')}
        />
      )}

      {modalState.isUserPreferencesOpen && (
        <UserEmailPreferences
          isOpen={modalState.isUserPreferencesOpen}
          setIsOpen={() => toggleModalState('isUserPreferencesOpen')}
          page={page}
        />
      )}
    </>
  )
}

export default PartnerGamesPage

export async function getServerSideProps({
  params,
  req,
  res,
}: GetServerSidePropsContext<{ slug?: string }>) {
  const { identifier, redirect } = getPartnerIdentifier({
    params,
    req,
    res,
  } as GetServerSidePropsContext<{ slug?: string }>)

  if (redirect) {
    return { redirect }
  }

  try {
    const pageResponse = await axios.get<PartnerPageData>(
      `${BASE_API_URL}/b2b/partner-pages/${identifier}`,
    )

    let game: GameINFOV2 | null = null
    try {
      game = pageResponse.data.game_id
        ? (await axios.get<GameINFOV2>(`${BASE_API_URL}/games/v2/${pageResponse.data.game_id}`))
            .data
        : null
    } catch (error) {
      /* empty */
    }

    const page = pageResponse.data

    let gamesData: PartnerGamesResponse | null = null
    let error: string | null = null

    try {
      const gamesResponse = await axios.get<PartnerGamesResponse>(
        `${BASE_API_URL}/games/v2/partners/${page.id}/games`,
      )
      gamesData = gamesResponse.data
    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to fetch games'
    }

    const shouldRenderDarkLogo = checkContrast(pageResponse.data.landing_bg_color)

    return {
      props: {
        page,
        game: game ?? null,
        shouldRenderDarkLogo,
        gamesData,
        error,
      },
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to fetch partner page:', error)
    return {
      notFound: true,
    }
  }
}
